import { UniswapV3PriceProvider } from '@silo/typechain';
import {
  ChainIdLegacy,
  ChecksumAddress,
  SupportedChainId,
  SupportedChainValue,
  USD_DECIMALS,
  getChecksumAddressOrThrow,
  isSupportedChainId,
} from '@silo/types';
import { queryCoinGeckoPrice } from 'app/queries/price/useQueryCoinGeckoPrice';
import { BASE_PRICE_POOL, CHAINLINK_QUOTE_PRICE_DECIMALS } from 'app/config/networks';
import { isNullOrUndefined, shiftDecimal } from '@silo/utils';
import { Contract } from '@ethersproject/contracts';
import { FallbackProvider } from '@ethersproject/providers';
import { CHAINLINK_ETH_USD_ABI } from '@silo/contract-utils';
import { BigNumber } from '@ethersproject/bignumber';
import { chainlinkEthUsdFeedAddress } from '@silo/contract-addresses';

export const promiseFetchQuotePrice = async ({
  chainId,
  provider,
  contracts,
}: {
  chainId: ChainIdLegacy;
  provider: FallbackProvider;
  contracts: { uniswapV3PriceProvider: UniswapV3PriceProvider };
}) => {
  if (process.env.NEXT_PUBLIC_QUOTE_PRICE_SOURCE === 'COIN_GECKO') {
    return queryCoinGeckoPrice('ethereum');
  }

  if (!isSupportedChainId(chainId)) {
    throw new Error(`Unsupported chainId: ${chainId}`);
  }

  try {
    const decimals = CHAINLINK_QUOTE_PRICE_DECIMALS[chainId];
    if (isNullOrUndefined(decimals)) {
      throw new Error('No chainlink quote price decimals defined for chainId: ' + chainId);
    }

    const price: BigNumber = await queryQuotePriceFromChainlink(chainId, provider);
    const shifted = shiftDecimal(price.toBigInt(), USD_DECIMALS - decimals);

    return { value: shifted, decimals: USD_DECIMALS };
  } catch (error) {
    console.warn('Failed to query quote price from chainlink feed, falling back to uniswap price provider', error);

    const price = await contracts.uniswapV3PriceProvider.quotePrice(BASE_PRICE_POOL[chainId]);
    return { value: price.toBigInt(), decimals: USD_DECIMALS };
  }
};

const queryQuotePriceFromChainlink = (chainId: SupportedChainId, provider: FallbackProvider) => {
  const chainlinkPriceFeed = new Contract(ChainlinkETH_USDFeed[chainId], CHAINLINK_ETH_USD_ABI, provider);

  return chainlinkPriceFeed.latestAnswer();
};

/**
 * These are Chainlink ETH/USDs feeds. To add a new chain, look here: https://data.chain.link/feeds
 */
const ChainlinkETH_USDFeed: SupportedChainValue<ChecksumAddress> = {
  [ChainIdLegacy.MAINNET]: getChecksumAddressOrThrow(chainlinkEthUsdFeedAddress.ethereum),
  [ChainIdLegacy.LOCAL]: getChecksumAddressOrThrow(chainlinkEthUsdFeedAddress.local),
  [ChainIdLegacy.ARBITRUM]: getChecksumAddressOrThrow(chainlinkEthUsdFeedAddress.arbitrum),
  [ChainIdLegacy.OPTIMISM]: getChecksumAddressOrThrow(chainlinkEthUsdFeedAddress.optimism),
  [ChainIdLegacy.BASE]: getChecksumAddressOrThrow(chainlinkEthUsdFeedAddress.base),
  [ChainIdLegacy.ANVIL]: getChecksumAddressOrThrow(chainlinkEthUsdFeedAddress.anvil),
};
